import {AxiosError} from "axios";
import {useEffect, useState} from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {PGPApi, PGPKeyDetails} from "../api/admin/generated";
import {Pager} from "../common/Pager";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {SettingsLevel} from "../settings/SettingsUtils";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";
import {PGPKeyRow} from "./PGPKeyRow";
import {PGPKeyTableHeader} from "./PGPKeyTableHeader";

const pgpApi = new PGPApi(ApiConfig.config());

interface PGPEncryptionKeysProps {
    settingsLevel : SettingsLevel
    settingsLevelSubSelector : string
}

export const PGPEncryptionKeys = ({settingsLevel, settingsLevelSubSelector} : PGPEncryptionKeysProps) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * The encryption keys
     */
    const [encryptionKeys, setEncryptionKeys] = useState<PGPKeyDetails[]>();
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of certificates to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(20);
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    /**
     * Load all encryption keys
     */
    useEffect(() => {
        logger.debug("Getting encryption keys");

        let callback;

        switch (settingsLevel)
        {
            case SettingsLevel.User:
                callback = async () => {
                    return await pgpApi.pGPControllerGetEncryptionKeysForEmail(
                        settingsLevelSubSelector);
                };
                break;
            case SettingsLevel.Domain:
                callback = async () => {
                    return await pgpApi.pGPControllerGetEncryptionKeysForDomain(
                        settingsLevelSubSelector);
                };
                break;
        }

        if (callback === undefined) {
            throw Error("callback is undefined");
        }

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        callback().then(response => {
                setEncryptionKeys(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting encryption keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        settingsLevel,
        settingsLevelSubSelector,
        t]);

    return (
        <>
            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table bordered hover>
                    <PGPKeyTableHeader/>
                    <tbody>
                    {encryptionKeys?.map((key : PGPKeyDetails) => (
                        <PGPKeyRow key={key.fingerprint} pgpKey={key}/>
                    ))}
                    </tbody>
                </Table>
                </Col>
            </Row>

            <Row className="mt-4 align-items-baseline">
                <div className="col-6 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {encryptionKeys?.length} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage}/>
                    </div>
                </div>
                <div className="col-6">
                    <div className="d-flex justify-content-end">
                        <Pager itemCount={encryptionKeys?.length??0}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}